import { BottomSheetModal } from '@gorhom/bottom-sheet';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { WEB_SHEET_MAX_WIDTH } from '@/constants/web';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { usePersonChangeHomeArea } from '@/features/api/hooks/participant/usePersonChangeHomeArea';
import { useGetSelectedGeoAreas } from '@/features/api/hooks/participant-geo-area/useGetSelectedGeoAreas';
import { useChangeSelectedGeoAreas } from '@/features/api/hooks/shared-app/useChangeSelectedGeoAreas';
import { GeoAreaSubscription } from '@/features/geo-area/components/subscription/GeoAreaSubscription';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { SheetBackdrop } from '@/shared/components/SheetBackdrop';
import { isWeb } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, webSideInset: number) =>
  StyleSheet.create({
    sheet: {
      flex: 1,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    sheetBackground: {
      backgroundColor: theme.colors.surfaceContainerLow
    },
    sheetHandleIndicator: {
      backgroundColor: theme.colors.outline
    },
    webSheet: {
      left: webSideInset,
      right: webSideInset,
      backgroundColor: theme.colors.surfaceContainerLow,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.15)',
      elevation: 8
    }
  });

type Props = {
  visible: boolean;
  onDismiss: () => void;
};

export const HomeAreaChangeSheet: FC<Props> = ({ visible, onDismiss }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const webSideInset = isWeb
    ? Math.max((windowWidth - WEB_SHEET_MAX_WIDTH) / 2, 0)
    : 0;
  const styles = useMemo(
    () => makeStyles(theme, webSideInset),
    [theme, webSideInset]
  );
  const sheetRef = useRef<BottomSheetModal>(null);
  const wasVisibleRef = useRef(false);

  const { data: person } = useGetPerson();
  const { data: selectedGeoAreas } = useGetSelectedGeoAreas();
  const { mutate: changeHomeArea, isPending: isChangeHomeAreaPending } =
    usePersonChangeHomeArea();
  const { mutate: changeGeoAreas, isPending: isChangeGeoAreasPending } =
    useChangeSelectedGeoAreas();

  const { t: tGeoArea } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'screens.geo-area-subscription'
  });

  useEffect(() => {
    // Edge-triggered: only present() on false->true and dismiss() on true->false.
    // A gesture/backdrop dismiss already unmounts the sheet and fires onDismiss
    // (which sets visible=false); firing dismiss() again on the already
    // dismissed modal corrupts @gorhom v5's internal mount/status state and
    // makes the next present() a no-op. Guarding on the edge prevents that.
    if (visible === wasVisibleRef.current) return;
    wasVisibleRef.current = visible;

    if (visible) {
      sheetRef.current?.present();
    } else {
      sheetRef.current?.dismiss();
    }
  }, [visible]);

  const handleSheetDismiss = useCallback(() => {
    // The sheet is already unmounted here (gesture/backdrop/programmatic).
    // Sync the edge ref so the follow-up visible=false from the parent does
    // not trigger a redundant dismiss() on the dismissed modal.
    wasVisibleRef.current = false;
    onDismiss();
  }, [onDismiss]);

  const selectedGeoAreaIds = useMemo(
    () =>
      selectedGeoAreas
        ?.filter(({ id, leaf }) => leaf === true && id !== person?.homeAreaId)
        ?.map(({ id }) => id) ?? [],
    [selectedGeoAreas, person?.homeAreaId]
  );

  const handleGeoAreaChange = useCallback(
    (id?: string, ids?: string[]) => {
      if (isEmptyOrNull(id)) return;

      changeHomeArea(
        { newHomeAreaId: id },
        {
          onSuccess: () =>
            changeGeoAreas(
              { newSelectedGeoAreaIds: [id, ...(ids ?? [])] },
              { onSuccess: onDismiss }
            )
        }
      );
    },
    [changeGeoAreas, changeHomeArea, onDismiss]
  );

  return (
    <BottomSheetModal
      ref={sheetRef}
      enableDynamicSizing={false}
      snapPoints={['90%']}
      topInset={insets.top}
      onDismiss={handleSheetDismiss}
      // Android + New Architecture: the content pan gesture (used to drag the
      // sheet from its scrollable content) conflicts with the ScrollView's
      // native scroll gesture and blocks scrolling entirely. Disabling it
      // keeps the handle drag/pan-down-to-close working while letting the
      // BottomSheetScrollView scroll normally.
      enableContentPanningGesture={false}
      backgroundStyle={
        isWeb ? { backgroundColor: 'transparent' } : styles.sheetBackground
      }
      style={isWeb ? styles.webSheet : undefined}
      handleIndicatorStyle={styles.sheetHandleIndicator}
      backdropComponent={SheetBackdrop}
    >
      <View style={styles.sheet}>
        <GeoAreaSubscription
          selectedGeoAreaId={person?.homeAreaId}
          selectedGeoAreaIds={selectedGeoAreaIds}
          onChange={handleGeoAreaChange}
          labels={tGeoArea('map', { returnObjects: true })}
          disableButton={isChangeHomeAreaPending || isChangeGeoAreasPending}
          stickyButton
          inBottomSheet
        />
      </View>
    </BottomSheetModal>
  );
};
